import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useIdentity } from '@bonusly/who-am-i';

import { getHttpClient, useHttpClient } from '../lib/http-client';
import { ShippingInformation } from '../schemas/identity';
import { headers } from './meta';

const QueryKey = {
	AddressLookup: 'identity:address-lookup',
	UserNotificationSettings: 'identity:user-notification-settings',
};

/*
 * Address Lookup
 */

export const addressLookupQueryKey = (params: Record<string, string>) => [QueryKey.AddressLookup, params];
export const userNotificationSettingsQueryKey = () => [QueryKey.UserNotificationSettings];

export const addressLookupPath = (prefix = '') => `${prefix}/identity/shipping_information/autocomplete`;
export const shippingInformationPath = (prefix = '') => `${prefix}/identity/shipping_information`;
export const shippingInformationValidationPath = (prefix = '') => `${prefix}/identity/shipping_information/validate`;

type ShippingInformationAPIData = {
	id: string;
	address1: string;
	address2: string;
	city: string;
	state: string;
	postalCode: string;
	country: string;
	phoneNumber: string;
};

export type UpdateShippingInformationAPIData = Omit<ShippingInformationAPIData, 'id'>;

export const useUpdateShippingInformation = () => {
	const { refetchProfile } = useIdentity();

	return useMutation({
		mutationFn: async (data: UpdateShippingInformationAPIData) => {
			const http = await getHttpClient();

			const res = await http.put<ShippingInformationAPIData>(shippingInformationPath(), data, { headers });

			refetchProfile();

			return ShippingInformation.parse(res.data);
		},
	});
};

type ShippingInformationValidationAPIData = {
	address1: string;
	address2: string;
	city: string;
	state: string;
	postalCode: string;
	country: string;
	phoneNumber: string;
};

type ShippingValidationResultAPIData = {
	valid: boolean;
	errors: string[];
	suggestions: {
		errorCode: string;
		message: string;
		path: string;
	}[];
};

type ValidateAddressOptions = {
	onError?: (error: unknown) => void;
	onSuccess?: (data: ShippingValidationResultAPIData) => void;
};

export const useValidateShippingInformation = (options: ValidateAddressOptions = {}) => {
	const http = useHttpClient();

	return useMutation({
		mutationFn: async (data: ShippingInformationValidationAPIData) => {
			const params = {
				...data,
				phone_number: data.phoneNumber,
				postal_code: data.postalCode,
			};

			try {
				const res = (await http.get<ShippingValidationResultAPIData>(shippingInformationValidationPath(), { params, headers })).data;
				options.onSuccess?.(res);
				return res;
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

export type AddressLookupAPIData = {
	address1: string;
	address2: string;
	city: string;
	state: string;
	postalCode: string;
};

export const useAddressLookup = (params: { address: string; country: string }) => {
	const http = useHttpClient();

	return useQuery({
		queryKey: addressLookupQueryKey(params),
		queryFn: async () => {
			return (await http.get<AddressLookupAPIData[]>(addressLookupPath(), { params, headers })).data;
		},
		enabled: params.address.length >= 4 && !!params.country,
	});
};

/*
 * User Settings
 */

/*
 * User Settings Types
 */

export interface EmailPreferences {
	sendBonusEmails: boolean;
	sendReminderEmails: boolean;
	sendCommentNotifications: boolean;
	sendActivityDigest: boolean;
	sendAdminRewardNotifications: boolean;
	sendClaimNotifications: boolean;
	sendManagerDigest: boolean;
	sendAdminMonthlyDigest: boolean;
	sendTrialReminders: boolean;
	sendOneOnOneRemindersAndUpdates: boolean;
	sendReviewRecapRemindersAndUpdates: boolean;
	sendConstructiveFeedbackNotifications: boolean;
	sendAnnouncementEmails: boolean;
	sendSpecialProgramEmails: boolean;
	receiveDirectReportRecognitionReminderNotifications: boolean;
}

export interface SlackPreferences {
	sendBonusNotifications: boolean;
	sendBonusAddonNotifications: boolean;
	sendAnnouncementNotifications: boolean;
	receiveBirthdayNotifications: boolean;
	receiveAnniversaryNotifications: boolean;
	receiveDirectReportImportantCelebrationNotifications: boolean;
	receiveDirectReportRecognitionReminderNotifications: boolean;
	sendReminderNotifications: boolean;
	sendOneOnOneNotifications: boolean;
}

export interface MicrosoftTeamsPreferences {
	sendBonusNotifications: boolean;
	sendBonusAddonNotifications: boolean;
	sendAnnouncementNotifications: boolean;
	receiveAnniversaryNotifications: boolean;
	receiveBirthdayNotifications: boolean;
	receiveDirectReportImportantCelebrationNotifications: boolean;
	receiveDirectReportRecognitionReminderNotifications: boolean;
	sendReminderNotifications: boolean;
	sendOneOnOneNotifications: boolean;
}

export type ColorModePreference = 'light' | 'dark' | 'system';

export interface AppearancePreferences {
	colorMode: ColorModePreference;
}

// Backend format for user notification settings
// Slack and Teams preferences can be null if the user hasn't had them created yet
export interface UserNotificationSettings {
	emailPreferences: EmailPreferences;
	slackPreferences: SlackPreferences | null;
	microsoftTeamsPreferences?: MicrosoftTeamsPreferences | null;
	appearancePreferences?: AppearancePreferences;
}

/**
 * Fetches the current user's settings
 */
export const useUserSettings = () => {
	const http = useHttpClient();

	return useQuery({
		queryKey: userNotificationSettingsQueryKey(),
		queryFn: async () => {
			const res = await http.get<UserNotificationSettings>('/user/settings', { headers });
			return res.data;
		},
	});
};

/**
 * Updates the current user's settings
 */
export const useUpdateUserSettings = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (settings: UserNotificationSettings) => {
			const res = await http.put<UserNotificationSettings>('/user/settings', settings, { headers });
			return res.data;
		},
		onSuccess: (data) => {
			queryClient.setQueryData(userNotificationSettingsQueryKey(), data);
		},
	});
};

/*
 * Appearance Preferences
 *
 * These hooks derive appearance preferences from the main useUserSettings query
 * to avoid duplicate API calls to /user/settings
 */

/**
 * Fetches the current user's appearance preferences
 * Derives data from the main user settings query to avoid duplicate API calls.
 * Returns null if no preferences have been set (allows frontend to distinguish
 * between "no preference" and "explicitly set to light")
 */
export const useAppearancePreferences = () => {
	const { data, isLoading, isSuccess, isError, error } = useUserSettings();

	return {
		data: data?.appearancePreferences ?? null,
		isLoading,
		isSuccess,
		isError,
		error,
	};
};

/**
 * Updates the current user's appearance preferences
 * Only sends appearance preferences to the backend, updates the main user settings cache
 */
export const useUpdateAppearancePreferences = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (appearancePreferences: AppearancePreferences) => {
			const res = await http.put<UserNotificationSettings>('/user/settings', { appearancePreferences }, { headers });
			return res.data;
		},
		onSuccess: (data) => {
			// Update the main user settings cache - this is the single source of truth
			queryClient.setQueryData(userNotificationSettingsQueryKey(), data);
		},
	});
};
